import React, { useState } from "react";
import ScoreTrace from "./ScoreTrace";

const DISPOSITION_BADGE = {
  straight_through: "bg-success",
  form_assist: "bg-warning text-dark",
  exception: "bg-danger",
};

const SEVERITY_BADGE = {
  hard_failure: "bg-danger",
  review_signal: "bg-warning text-dark",
};

// current_value/value is wiped to nil by a failing upstream layer, so fall
// back to original_value for display -- same rationale as
// MappedInvoicePreview's fieldValue.
const fieldDisplayValue = (field) => {
  if (!field) return null;
  const value = field.value;
  if (value !== null && value !== undefined && value !== "") return value;
  return field.original_value;
};

const ReasonCodes = ({ codes }) => {
  if (!codes || codes.length === 0) return null;
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: "4px" }}>
      {codes.map((c, i) => (
        <span
          key={i}
          className={`badge ${SEVERITY_BADGE[c.severity] || "bg-secondary"}`}
          style={{ fontSize: "10px" }}
          title={c.message}
        >
          {c.code}
        </span>
      ))}
    </div>
  );
};

// status of one evaluated rule: "fired" takes the rule's own severity color.
const CHECK_STATUS = {
  passed: { icon: "✓", label: "passed", className: "text-success" },
  skipped: { icon: "–", label: "skipped", className: "text-muted" },
};

const CheckStatus = ({ check }) => {
  if (check.status === "fired") {
    const color = check.severity === "hard_failure" ? "text-danger" : "";
    return (
      <span className={color} style={check.severity === "hard_failure" ? undefined : { color: "#997404" }}>
        ✗ fired
      </span>
    );
  }
  const s = CHECK_STATUS[check.status] || CHECK_STATUS.skipped;
  return (
    <span className={s.className}>
      {s.icon} {s.label}
    </span>
  );
};

// Every rule Layers::Validators evaluated for one field -- fired, passed or
// skipped -- with the values each decision was made on.
// Stacked rather than tabular: the Step 6 card is narrow, and a multi-column
// table squeezes the explanation into an unreadable sliver.
const CheckList = ({ checks, showPart = false }) => {
  if (!checks || checks.length === 0) return <div className="text-muted small p-2">No checks run.</div>;

  return (
    <div className="small px-2 py-1" style={{ background: "#f8f9fa" }}>
      {checks.map((c, i) => (
        <div key={i} className={i > 0 ? "border-top py-1" : "py-1"}>
          <div className="d-flex flex-wrap align-items-center" style={{ gap: "6px" }}>
            <span style={{ minWidth: "70px" }}>
              <CheckStatus check={c} />
            </span>
            {showPart && <span className="badge bg-light text-dark border">{c.part}</span>}
            <code style={{ fontSize: "11px", wordBreak: "break-all" }} title={c.message}>
              {c.code}
            </code>
            <span className="text-muted" style={{ fontSize: "10px" }}>
              {c.severity}
            </span>
          </div>
          <div className="text-body" style={{ paddingLeft: "76px" }}>
            {c.detail}
          </div>
        </div>
      ))}
    </div>
  );
};

const DetailsToggle = ({ open, onClick }) => (
  <button type="button" className="btn btn-link btn-sm p-0 text-nowrap" style={{ fontSize: "11px" }} onClick={onClick}>
    {open ? "▾ hide" : "▸ details"}
  </button>
);

const FieldRow = ({ label, field }) => {
  const [open, setOpen] = useState(false);
  if (!field) return null;
  const hasChecks = field.checks && field.checks.length > 0;

  return (
    <>
      <tr>
        <td className="text-muted">{label}</td>
        <td>{fieldDisplayValue(field) ?? "—"}</td>
        <td className="text-end">{field.score ?? "—"}</td>
        <td>
          <ReasonCodes codes={field.reason_codes} />
        </td>
        <td className="text-end">{hasChecks && <DetailsToggle open={open} onClick={() => setOpen(!open)} />}</td>
      </tr>
      {open && (
        <tr>
          <td colSpan={5} className="p-0">
            <CheckList checks={field.checks} />
            {field.score_trace && field.score_trace.length > 0 && (
              <div className="border-top">
                <ScoreTrace trace={field.score_trace} title={`How the ${label.toLowerCase()} match score (${field.score}) was reached`} />
              </div>
            )}
          </td>
        </tr>
      )}
    </>
  );
};

const LineItemRow = ({ item }) => {
  const [open, setOpen] = useState(false);

  return (
    <>
      <tr>
        <td>
          {fieldDisplayValue(item.item_number) ?? "—"}
          <ReasonCodes codes={item.item_number && item.item_number.reason_codes} />
        </td>
        <td>{fieldDisplayValue(item.description) ?? "—"}</td>
        <td className="text-end">
          {fieldDisplayValue(item.quantity) ?? "—"}
          <ReasonCodes codes={item.quantity && item.quantity.reason_codes} />
        </td>
        <td className="text-end">{fieldDisplayValue(item.unit_price) ?? "—"}</td>
        <td className="text-end">
          {fieldDisplayValue(item.subtotal) ?? "—"}
          <ReasonCodes codes={item.subtotal && item.subtotal.reason_codes} />
        </td>
        <td>
          <ReasonCodes codes={item.reason_codes} />
        </td>
        <td className="text-end">
          {item.checks && item.checks.length > 0 && <DetailsToggle open={open} onClick={() => setOpen(!open)} />}
        </td>
      </tr>
      {open && (
        <tr>
          <td colSpan={7} className="p-0">
            <CheckList checks={item.checks} showPart />
          </td>
        </tr>
      )}
    </>
  );
};

const LineItemsChecks = ({ checks }) => {
  const [open, setOpen] = useState(false);
  if (!checks || checks.length === 0) return null;

  return (
    <div className="small mb-1">
      <span className="text-muted">Line item checks </span>
      <DetailsToggle open={open} onClick={() => setOpen(!open)} />
      {open && (
        <div className="border rounded mt-1">
          <CheckList checks={checks} />
        </div>
      )}
    </div>
  );
};

// VendorInsights facts (see Layers::Validator#classify_vendor_history) --
// null when there's no resolved vendor or no location to check against.
const VendorHistoryPanel = ({ vendorHistory }) => {
  if (!vendorHistory) return null;

  const { invoice_count: invoiceCount, used_within_days: usedRecently, window_days: windowDays, similar_vendors: similarVendors } =
    vendorHistory;

  return (
    <div className="small border rounded p-2 mb-2" style={{ background: "#f8f9fa" }}>
      <div className="text-muted text-uppercase mb-1" style={{ fontSize: "10px" }}>
        Vendor history at this location
      </div>
      <div>
        {invoiceCount} invoice{invoiceCount === 1 ? "" : "s"} on file
        {invoiceCount > 0 && (
          <>
            {" "}
            &middot;{" "}
            {usedRecently ? `used within the last ${windowDays} days` : `none in the last ${windowDays} days`}
          </>
        )}
      </div>
      {similarVendors && similarVendors.length > 0 && (
        <div className="mt-1">
          <span className="text-muted">Similarly named vendors already invoiced here: </span>
          {similarVendors.map((v, i) => (
            <span key={i} className="badge bg-secondary ms-1" title={`similarity ${v.similarity}`}>
              {v.name}
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

// VendorInsights total_history (see Layers::Validators::InvoiceTotal) --
// null when there's no resolved vendor/location, or no prior invoices to
// compare against.
const TotalHistoryPanel = ({ totalHistory }) => {
  if (!totalHistory) return null;

  return (
    <div className="small border rounded p-2 mb-2" style={{ background: "#f8f9fa" }}>
      <div className="text-muted text-uppercase mb-1" style={{ fontSize: "10px" }}>
        Total amount history at this location
      </div>
      <div>
        Average of {totalHistory.count} prior invoice{totalHistory.count === 1 ? "" : "s"}: $
        {totalHistory.average.toFixed(2)}
      </div>
    </div>
  );
};

const CHARGE_FIELDS = [
  ["sales_tax", "Sales Tax"],
  ["surcharge", "Surcharge"],
  ["shipping_fee", "Shipping"],
  ["deposit", "Deposit"],
  ["other_fees", "Other Fees"],
  ["discount", "Discount"],
  ["credit", "Credit"],
];

// Table-shaped view of Layers::Validator's output (Step 6). Every header
// field is { value, original_value, score, passed, ref, components, notes,
// reason_codes: [{code, severity, message}] } -- Validator's own evidence
// shape, distinct from both InvoicePreview's flat extraction and
// MappedInvoicePreview's raw UnitData shape.
const ValidationPreview = ({ data }) => {
  const fields = data.fields || {};
  const lineItems = fields.line_items || [];
  const charges = CHARGE_FIELDS.filter(([key]) => fields[key] && fieldDisplayValue(fields[key]) !== null);

  return (
    <div className="border rounded p-3 bg-white mt-2">
      <div className="d-flex align-items-center justify-content-between border-bottom pb-2 mb-2">
        <div>
          <span className={`badge ${DISPOSITION_BADGE[data.disposition] || "bg-secondary"}`}>{data.disposition}</span>
          <span className="text-muted small ms-2">score {data.score}</span>
        </div>
        <div className="text-muted small">
          {(data.hard_failures || []).length} hard failure(s) · {(data.review_signals || []).length} review signal(s)
        </div>
      </div>

      <div className="table-responsive">
        <table className="table table-sm table-bordered mb-2">
          <thead className="table-light">
            <tr>
              <th>Field</th>
              <th>Value</th>
              <th className="text-end">Score</th>
              <th>Reason codes</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            <FieldRow label="Location" field={fields.location} />
            <FieldRow label="Vendor" field={fields.vendor} />
            <FieldRow label="Invoice #" field={fields.invoice_number} />
            <FieldRow label="Invoice date" field={fields.invoice_date} />
            <FieldRow label="Total amount" field={fields.total_amount} />
            {charges.map(([key, label]) => (
              <FieldRow key={key} label={label} field={fields[key]} />
            ))}
          </tbody>
        </table>
      </div>

      <VendorHistoryPanel vendorHistory={data.evidence && data.evidence.vendor_history} />
      <TotalHistoryPanel totalHistory={data.evidence && data.evidence.total_history} />

      <LineItemsChecks checks={data.line_items_checks} />

      {lineItems.length > 0 ? (
        <div className="table-responsive">
          <table className="table table-sm table-bordered mb-2">
            <thead className="table-light">
              <tr>
                <th>Item #</th>
                <th>Description</th>
                <th className="text-end">Qty</th>
                <th className="text-end">Unit Price</th>
                <th className="text-end">Subtotal</th>
                <th>Reason codes</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {lineItems.map((item, i) => (
                <LineItemRow key={i} item={item} />
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="text-muted small mb-2">No line items captured.</div>
      )}

      {(data.hard_failures || []).length > 0 && (
        <ul className="small text-danger mb-0 mt-2">
          {data.hard_failures.map((f, i) => (
            <li key={i}>
              [{f.field}] {f.message}
            </li>
          ))}
        </ul>
      )}
      {(data.review_signals || []).length > 0 && (
        <ul className="small mb-0 mt-2" style={{ color: "#997404" }}>
          {data.review_signals.map((f, i) => (
            <li key={i}>
              [{f.field}] {f.message}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default ValidationPreview;
